import { ContentCopy, UpdateOutlined, WarningAmber } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type React from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useTemplateNurseWeeklyOT } from "../../../../hooks/useRecurringShiftOvertimeMap";
import { STATUS_BADGE } from "../../../../theme/tokens";
import { formatEmployeeStatusReason } from "../../../../utils/employeeStatusReason";
import { getNurseEffectiveStatus, getNurseStatusReason } from "../../../../utils/formatters";
import { EffectiveEmployeeStatus, type Nurse } from "../../../../utils/models";
import { getShiftCellStyle, gridShiftCellInnerStyle, toggleMasterScheduleNurseCellHighlight } from "../utils";

const STRIKETHROUGH_PAUSED_SX = { textDecoration: "line-through", opacity: 0.7 } as const;

export type ClickableShiftCellProps = {
	nurseId: number;
	recurringShiftId: number | undefined;
	multipleShifts: boolean;
	onShiftKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
	onShiftClick: (e: React.MouseEvent<HTMLElement>) => void;
	isFutureShift: boolean;
	recurringShiftStartDate: string;
	relevantNurse: Nurse | undefined;
	timeOffDuringShift?: { ptoStartDate: DateTime; ptoEndDate?: DateTime };
	/**
	 * Indicates whether a drag operation is currently happening in the master schedule context.
	 */
	isDraggingContext?: boolean;
	/**
	 * Indicates whether this specific shift cell is being dragged.
	 */
	isDraggingSelf?: boolean;
	/**
	 * Indicates whether the user is copying (e.g., holding Ctrl or Cmd) during a drag operation.
	 */
	isCopying?: boolean;
	/**
	 * Indicates whether this shift was added optimistically (i.e., before server confirmation).
	 */
	optimisticallyAddedShift?: boolean;
	isSynergyMS: boolean;
	/**
	 * Indicates whether the staff member is credentialed for this facility - used for synergy-like departments
	 */
	isCredentialed: boolean;
	/**
	 * Any date inside the cell's week; used to scope the OT check. Omitted for the drag overlay ghost.
	 */
	referenceDate?: DateTime;
	/**
	 * Root element id used to scope the same-nurse hover highlight. Defaults to the V2 root; the
	 * Consecutive Master Schedule passes its own root id.
	 */
	highlightRootId?: string;
};

// Memoized: during a drag, dnd-kit re-renders the DraggableShift/DroppableCell wrappers on every
// pointer move. This leaf does the heavy work (OT query, tooltips, style/name computation), so we
// shallow-compare props and bail when nothing changed — its inputs are referentially stable across a
// pure move (stable handlers from DraggableShift, stable per-cell data while buildScheduleData isn't
// rebuilding). Without this, ~1.5k cells re-rendered deeply on every move and froze the UI.
export const ClickableShiftCell = memo<ClickableShiftCellProps>(
	({
		recurringShiftId,
		onShiftClick,
		onShiftKeyDown,
		multipleShifts,
		nurseId,
		isFutureShift,
		recurringShiftStartDate,
		relevantNurse,
		timeOffDuringShift,
		isDraggingContext,
		isDraggingSelf,
		isCopying,
		optimisticallyAddedShift,
		isSynergyMS,
		isCredentialed,
		referenceDate,
		highlightRootId,
	}) => {
		const { t } = useTranslation();
		const { chosenDepartment } = useChosenDepartment();
		const formatPersonName = useFormatPersonName();
		// DEFAULT: no name while the nurse isn't passed in; every caller passes the nurse the shift belongs to.
		const formattedName = relevantNurse ? formatPersonName(relevantNurse) : "";
		const [isHovering, setIsHovering] = useState<boolean>(false);

		// On Leave / Suspended nurses keep their master-schedule spot but are visually marked
		// (strikethrough name + reason in the tooltip) so the admin knows the slot is paused.
		const effectiveStatus = relevantNurse ? getNurseEffectiveStatus(relevantNurse, chosenDepartment.department_id) : null;
		const isOnLeaveOrSuspended = effectiveStatus === EffectiveEmployeeStatus.OnLeave || effectiveStatus === EffectiveEmployeeStatus.Suspended;
		const statusReason = relevantNurse ? formatEmployeeStatusReason(getNurseStatusReason(relevantNurse, chosenDepartment.department_id), t) : null;
		const statusFallbackLabel =
			effectiveStatus === EffectiveEmployeeStatus.Suspended ? t("employee.status.chip.suspended") : t("employee.status.chip.on_leave");

		const handleMouseOver = useCallback(() => {
			setIsHovering(true);
			toggleMasterScheduleNurseCellHighlight(nurseId, true, highlightRootId);
		}, [nurseId, highlightRootId]);

		const handleMouseOut = useCallback(() => {
			setIsHovering(false);
			toggleMasterScheduleNurseCellHighlight(nurseId, false, highlightRootId);
		}, [nurseId, highlightRootId]);

		useEffect(() => {
			if (isDraggingContext) {
				handleMouseOut();
			}
		}, [isDraggingContext, handleMouseOut]);

		const renderOT = useTemplateNurseWeeklyOT({
			nurseId,
			referenceDate,
			enabled: !isSynergyMS,
		});

		return (
			<Box
				key={recurringShiftId}
				sx={getShiftCellStyle(multipleShifts, !!timeOffDuringShift)}
				tabIndex={0}
				onKeyDown={(event) => onShiftKeyDown(event)}
				onClick={(event) => onShiftClick(event)}
				onMouseOver={isDraggingContext ? undefined : handleMouseOver}
				onMouseOut={isDraggingContext ? undefined : handleMouseOut}
				data-nurse-id={/*We use data-nurse-id to toggle css classes without re-render of React to highlight the cells of the hovered nurse*/ nurseId}
				data-testid={`master-schedule-shift-cell-${recurringShiftId ?? "new"}-${nurseId}`}
			>
				<InHouseTooltip
					disableHoverListener={isDraggingContext}
					disableFocusListener={isDraggingContext}
					disableTouchListener={isDraggingContext}
					title={`${formattedName} ${timeOffDuringShift ? `(${t("time.off")} from ${timeOffDuringShift.ptoStartDate.toFormat("MM/dd")} to ${timeOffDuringShift.ptoEndDate?.toFormat("MM/dd") ?? ""})` : ""}${isOnLeaveOrSuspended ? ` — ${statusReason ? t("employee.status.chip.reason", { reason: statusReason }) : statusFallbackLabel}` : ""}`}
				>
					<Box className="ms2-shift-cell-inner" sx={[gridShiftCellInnerStyle, isOnLeaveOrSuspended ? STRIKETHROUGH_PAUSED_SX : null]}>
						{formattedName}
					</Box>
				</InHouseTooltip>
				{isFutureShift && (
					<InHouseTooltip title={`This recurrence is scheduled for a future date and will begin recurring on the ${recurringShiftStartDate}`}>
						<UpdateOutlined fontSize={"inherit"} />
					</InHouseTooltip>
				)}
				{isSynergyMS && !isCredentialed && (
					<InHouseTooltip title="This staff member is not credentialed">
						<WarningAmber fontSize={"inherit"} sx={{ color: STATUS_BADGE.ORANGE }} />
					</InHouseTooltip>
				)}
				{isHovering && <EditIcon fontSize={"inherit"} />}
				{isDraggingSelf && isCopying && <ContentCopy fontSize={"inherit"} />}
				{optimisticallyAddedShift && (
					<Box
						sx={{
							height: "1em",
							width: "1em",
							pl: 1,
						}}
					>
						<InHouseTooltip title={"Shift was moved or copied here, awaiting server confirmation"}>
							<CircularProgress size={"10px"} />
						</InHouseTooltip>
					</Box>
				)}
				{!isHovering && renderOT && (
					<Typography
						sx={{
							fontWeight: 600,
							fontSize: {
								xs: "6px",
								xl: "12px",
							},
							color: STATUS_BADGE.CORAL,
						}}
					>
						OT
					</Typography>
				)}
			</Box>
		);
	},
);

ClickableShiftCell.displayName = "ClickableShiftCell";
